/**
 * Mapping des régions normalisées vers leurs mots-clés de recherche.
 * Permet de trouver les rappels avec des variations de noms et formats.
 * Gère aussi les chaînes pipe-separated comme "guadeloupe|martinique|guyane"
 */
export const REGION_KEYWORDS: Record<string, string[]> = {
  // Métropole - régions administratives avec numéros de départements
  'auvergne-rhône-alpes': [
    'auvergne', 'rhône-alpes', 'rhone-alpes', 'auvergne-rhône-alpes',
    '01', '03', '07', '15', '26', '38', '42', '43', '63', '69', '73', '74',
    'ain', 'allier', 'ardèche', 'cantal', 'drôme', 'isère', 'loire', 'haute-loire', 
    'puy-de-dôme', 'rhône', 'savoie', 'haute-savoie'
  ],
  'bourgogne-franche-comté': [
    'bourgogne', 'franche-comté', 'franche-comte', 'bourgogne-franche-comté',
    '21', '25', '39', '58', '70', '71', '89', '90',
    'côte-d\'or', 'doubs', 'jura', 'nièvre', 'haute-saône', 'saône-et-loire', 'yonne', 'territoire de belfort'
  ],
  'bretagne': [
    'bretagne',
    '22', '29', '35', '56',
    'côtes-d\'armor', 'finistère', 'ille-et-vilaine', 'morbihan'
  ],
  'centre-val de loire': [
    'centre', 'val de loire', 'centre-val de loire',
    '18', '28', '36', '37', '41', '45',
    'cher', 'eure-et-loir', 'indre', 'indre-et-loire', 'loir-et-cher', 'loiret'
  ],
  'corse': [
    'corse', 'corse-du-sud', 'haute-corse',
    '2a', '2b', '20'
  ],
  'grand est': [
    'grand est', 'alsace', 'lorraine', 'champagne-ardenne', 'champagne',
    '08', '10', '51', '52', '54', '55', '57', '67', '68', '88',
    'ardennes', 'aube', 'marne', 'haute-marne', 'meurthe-et-moselle', 'meuse', 'moselle', 'bas-rhin', 'haut-rhin', 'vosges'
  ],
  'hauts-de-france': [
    'hauts-de-france', 'nord-pas-de-calais', 'picardie', 'nord',
    '02', '59', '60', '62', '80',
    'aisne', 'oise', 'pas-de-calais', 'somme'
  ],
  'île-de-france': [
    'île-de-france', 'ile-de-france', 'paris', 'idf',
    '75', '77', '78', '91', '92', '93', '94', '95',
    'seine-et-marne', 'yvelines', 'essonne', 'hauts-de-seine', 'seine-saint-denis', 'val-de-marne', 'val-d\'oise'
  ],
  'normandie': [
    'normandie', 'haute-normandie', 'basse-normandie',
    '14', '27', '50', '61', '76',
    'calvados', 'eure', 'manche', 'orne', 'seine-maritime'
  ],
  'nouvelle-aquitaine': [
    'nouvelle-aquitaine', 'aquitaine', 'limousin', 'poitou-charentes',
    '16', '17', '19', '23', '24', '33', '40', '47', '64', '79', '86', '87',
    'charente', 'charente-maritime', 'corrèze', 'creuse', 'dordogne', 'gironde', 'landes', 'lot-et-garonne', 'pyrénées-atlantiques', 'deux-sèvres', 'vienne', 'haute-vienne'
  ],
  'occitanie': [
    'occitanie', 'midi-pyrénées', 'languedoc-roussillon', 'languedoc',
    '09', '11', '12', '30', '31', '32', '34', '46', '48', '65', '66', '81', '82',
    'ariège', 'aude', 'aveyron', 'gard', 'haute-garonne', 'gers', 'hérault', 'lot', 'lozère', 'hautes-pyrénées', 'pyrénées-orientales', 'tarn', 'tarn-et-garonne'
  ],
  'pays de la loire': [
    'pays de la loire', 'pays-de-la-loire',
    '44', '49', '53', '72', '85',
    'loire-atlantique', 'maine-et-loire', 'mayenne', 'sarthe', 'vendée'
  ],
  'provence-alpes-côte d\'azur': [
    'provence', 'paca', 'côte d\'azur', 'cote d\'azur', 'provence-alpes-côte d\'azur',
    '04', '05', '06', '13', '83', '84',
    'alpes-de-haute-provence', 'hautes-alpes', 'alpes-maritimes', 'bouches-du-rhône', 'var', 'vaucluse'
  ],

  // DOM-TOM - tous les territoires d'outre-mer
  'dom-tom': [
    'guadeloupe', 'guyane', 'martinique', 'mayotte', 
    'réunion', 'reunion', 'la réunion', 'la reunion',
    'outre-mer', 'outremer', 'dom', 'tom', 'drom', 'com',
    'saint-martin', 'saint martin', 'sint maarten',
    'saint-barthélemy', 'saint barthelemy', 'st-barth',
    'saint-pierre', 'saint pierre', 'miquelon',
    'wallis', 'futuna', 'wallis-et-futuna',
    'polynésie', 'polynesie', 'polynésie française', 'tahiti',
    'nouvelle-calédonie', 'nouvelle caledonie', 'nouméa',
    '971', '972', '973', '974', '976'
  ],
  
  // Territoires individuels pour sélection fine
  'guadeloupe': ['guadeloupe', '971'],
  'martinique': ['martinique', '972'],
  'guyane': ['guyane', '973', 'guyane française'],
  'la réunion': ['réunion', 'reunion', 'la réunion', 'la reunion', '974'],
  'mayotte': ['mayotte', '976'],
};

/**
 * Mots-clés pour identifier les rappels nationaux (France entière)
 * Ces mots-clés sont TOUJOURS ajoutés aux recherches régionales
 * pour inclure les rappels qui concernent tout le territoire
 */
export const NATIONAL_KEYWORDS: string[] = [
  'france entière',
  'france entiere', 
  'national',
  'tout le territoire',
  'territoire national',
  'métropole et outre-mer',
  'metropole et outre-mer',
  'france métropolitaine et outre-mer'
];

/**
 * Récupère tous les mots-clés pour une liste de régions sélectionnées
 * Inclut automatiquement les mots-clés nationaux pour capturer les rappels "France entière"
 */
export function getRegionKeywords(selectedRegions: string[]): string[] {
  const keywords: string[] = [];
  const DOM_TOM_KEYS = ['dom-tom', 'guadeloupe', 'martinique', 'guyane', 'la réunion', 'mayotte'];
  let hasMetropoleRegion = false;
  
  for (const region of selectedRegions) {
    const regionKey = region.toLowerCase();
    const regionKeywords = REGION_KEYWORDS[regionKey];
    if (regionKeywords) {
      keywords.push(...regionKeywords);
    } else {
      // Fallback: utiliser le nom de la région tel quel
      keywords.push(region.toLowerCase());
    }
    
    // Vérifier si c'est une région métropolitaine
    if (!DOM_TOM_KEYS.includes(regionKey)) {
      hasMetropoleRegion = true;
    }
  }
  
  // Ajouter les mots-clés nationaux SEULEMENT si une région métropole est sélectionnée
  // Pour DOM-TOM seul, on veut uniquement les rappels spécifiques
  if (hasMetropoleRegion) {
    keywords.push(...NATIONAL_KEYWORDS);
  }
  
  return keywords;
}

/**
 * Liste des régions métropolitaines pour l'affichage dans les filtres
 */
export const METROPOLE_REGIONS = [
  'Auvergne-Rhône-Alpes',
  'Bourgogne-Franche-Comté',
  'Bretagne',
  'Centre-Val de Loire',
  'Corse',
  'Grand Est',
  'Hauts-de-France',
  'Île-de-France',
  'Normandie',
  'Nouvelle-Aquitaine',
  'Occitanie',
  'Pays de la Loire',
  'Provence-Alpes-Côte d\'Azur'
];
